"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import type { PostHog } from "posthog-js";

/**
 * PostHog client-side provider.
 *
 * Initializes the PostHog browser SDK once on mount. Gated on the
 * presence of NEXT_PUBLIC_POSTHOG_KEY — missing key means no init and
 * no events (no-op), so local dev and PR previews don't spam a project.
 *
 * Also fires a `$pageview` event on every App Router navigation.
 * PostHog's browser SDK autocaptures $pageview on initial load, but
 * the App Router's client-side nav is invisible to it — we observe
 * pathname/searchParams changes and `capture` manually.
 *
 * IMPORTANT — privacy (Week 1 Sprint acceptance #8): no PII in any
 * event payload. The autocapture config is explicit about what we
 * collect, and `person_profiles: 'identified_only'` means anonymous
 * visitors don't get a PostHog person record until we explicitly
 * identify them (which we will only do post-signup, with the parent's
 * Supabase user id — never their email or name).
 *
 * Perf (§14): `posthog-js` is ~60 kB minified + gzipped and is pure
 * analytics — it must never compete with LCP. The SDK is dynamically
 * imported inside the mount effect so it ships in its own lazy chunk,
 * loaded after hydration. The type-only import above keeps tsc happy
 * without emitting any runtime code for the library.
 *
 * See FOLLOWUPS.md #4 for the steps to create the PostHog project
 * and wire the key.
 */
// Cached SDK handle, shared across the init effect and every
// subsequent pageview effect. `null` means "not loaded yet or disabled."
let phClient: PostHog | null = null;

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const pendingPageview = useRef<string | null>(null);
  const [posthogReady, setPosthogReady] = useState(false);

  // Init once on mount — dynamic import so posthog-js doesn't ship
  // in the main bundle. Further deferred to `requestIdleCallback` so
  // the chunk isn't even fetched until the browser is idle
  // post-LCP: analytics must never compete with first paint (§14).
  useEffect(() => {
    const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
    const host = process.env.NEXT_PUBLIC_POSTHOG_HOST;

    if (!key) {
      return;
    }
    if (phClient) {
      return;
    }

    let cancelled = false;

    const init = () => {
      if (cancelled) return;
      void import("posthog-js").then(({ default: posthog }) => {
        if (cancelled) return;
        posthog.init(key, {
          api_host: host ?? "https://us.i.posthog.com",
          person_profiles: "identified_only",
          capture_pageview: false, // manual pageview below covers client nav
          capture_pageleave: true,
          autocapture: false, // explicit events only, no DOM autocapture
          disable_session_recording: true,
          capture_exceptions: true,
          loaded: async (ph) => {
            setPosthogReady(true);
            if (process.env.NODE_ENV === "development") {
              ph.debug(false);
            }
            // Identify the logged-in user so client-side events are
            // correlated with the server-side events in lib/analytics.ts.
            // We use the Supabase auth user id — never email or name (§13).
            try {
              const { getSupabaseBrowserClient } = await import(
                "@/lib/supabase-browser"
              );
              const supabase = getSupabaseBrowserClient();
              const auth = supabase.auth as {
                getUser: () => Promise<{
                  data: { user: { id?: string | null } | null };
                }>;
              };
              const {
                data: { user },
              } = await auth.getUser();
              if (user?.id) {
                let planType = "free";
                try {
                  const { data: sub } = await supabase
                    .from("premium_subscriptions")
                    .select("status")
                    .eq("user_id", user.id)
                    .eq("status", "active")
                    .maybeSingle();
                  if (sub) planType = "premium";
                } catch {}
                ph.identify(user.id, { plan_type: planType });
              }
            } catch (err) {
              // Non-critical — analytics must never break the page.
              console.error("[posthog-provider] identify failed", err);
            }
          },
        });
        phClient = posthog;
      });
    };

    const ric = (
      window as unknown as {
        requestIdleCallback?: (
          cb: () => void,
          opts?: { timeout: number },
        ) => number;
        cancelIdleCallback?: (id: number) => void;
      }
    ).requestIdleCallback;
    let idleId: number | undefined;
    let timeoutId: number | undefined;
    if (typeof ric === "function") {
      idleId = ric(init, { timeout: 3000 });
    } else {
      timeoutId = window.setTimeout(init, 1500);
    }

    return () => {
      cancelled = true;
      if (idleId !== undefined) {
        (
          window as unknown as { cancelIdleCallback?: (id: number) => void }
        ).cancelIdleCallback?.(idleId);
      }
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, []);

  // Manual $pageview on path/search change
  useEffect(() => {
    if (!process.env.NEXT_PUBLIC_POSTHOG_KEY) {
      return;
    }
    if (!pathname) {
      return;
    }
    const url = searchParams?.toString()
      ? `${pathname}?${searchParams.toString()}`
      : pathname;
    if (!phClient) {
      // SDK still loading — queue the most recent URL so the initial
      // pageview is replayed once the client is ready.
      pendingPageview.current = url;
      return;
    }
    phClient.capture("$pageview", { $current_url: url });
  }, [pathname, searchParams]);

  useEffect(() => {
    if (!posthogReady || !phClient || !pendingPageview.current) {
      return;
    }
    phClient.capture("$pageview", {
      $current_url: pendingPageview.current,
    });
    pendingPageview.current = null;
  }, [posthogReady]);

  return <>{children}</>;
}
